import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { filterData, getInfo } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';

const FilterOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [conditions, setConditions] = useState([{
    column: '',
    operator: 'equals',
    value: '',
    logic: 'and'
  }]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadColumns = async () => {
      try {
        const result = await getInfo();
        setColumns(result.info.column_names || []);
        if (result.info.column_names.length > 0) {
          setConditions([{ ...conditions[0], column: result.info.column_names[0] }]);
        }
      } catch (error) {
        toast.error(t('form.loadColumnsFailed'));
      }
    };
    loadColumns();
  }, []);

  const addCondition = () => {
    setConditions([...conditions, {
      column: columns[0] || '',
      operator: 'equals',
      value: '',
      logic: 'and'
    }]);
  };

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index, field, value) => {
    const updated = [...conditions];
    updated[index][field] = value;
    setConditions(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await filterData(conditions);
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('filter.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {conditions.map((condition, idx) => (
          <div key={idx} className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Condition {idx + 1}</span>
              {conditions.length > 1 && (
                <button type="button" onClick={() => removeCondition(idx)} className="btn-ghost h-6 w-6 p-0">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="label mb-1">{t('form.column')}</label>
                <select
                  value={condition.column}
                  onChange={(e) => updateCondition(idx, 'column', e.target.value)}
                  className="select"
                  required
                >
                  {columns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label mb-1">{t('filter.operator')}</label>
                <select
                  value={condition.operator}
                  onChange={(e) => updateCondition(idx, 'operator', e.target.value)}
                  className="select"
                  required
                >
                  <option value="equals">{t('filter.op.equals')}</option>
                  <option value="not_equals">{t('filter.op.notEquals')}</option>
                  <option value="contains">{t('filter.op.contains')}</option>
                  <option value="starts_with">{t('filter.op.startsWith')}</option>
                  <option value="ends_with">{t('filter.op.endsWith')}</option>
                  <option value="gt">{t('filter.op.gt')}</option>
                  <option value="lt">{t('filter.op.lt')}</option>
                  <option value="gte">{t('filter.op.gte')}</option>
                  <option value="lte">{t('filter.op.lte')}</option>
                  <option value="is_null">{t('filter.op.isNull')}</option>
                  <option value="not_null">{t('filter.op.notNull')}</option>
                </select>
              </div>
              {!['is_null', 'not_null'].includes(condition.operator) && (
                <div>
                  <label className="label mb-1">{t('filter.value')}</label>
                  <input
                    type="text"
                    value={condition.value}
                    onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                    className="input"
                    required
                  />
                </div>
              )}
            </div>
            {idx < conditions.length - 1 && (
              <div>
                <label className="label mb-1">{t('filter.nextLogic')}</label>
                <select
                  value={condition.logic}
                  onChange={(e) => updateCondition(idx, 'logic', e.target.value)}
                  className="select"
                >
                  <option value="and">{t('filter.and')}</option>
                  <option value="or">{t('filter.or')}</option>
                </select>
              </div>
            )}
          </div>
        ))}
      </div>
      <button type="button" onClick={addCondition} className="btn-secondary flex items-center gap-2">
        <Plus className="h-4 w-4" />
        Add Condition
      </button>
      <div className="flex gap-2 pt-4 border-t">
        <button type="submit" disabled={loading} className="btn-primary flex-1" data-testid="apply-filter">
          {loading ? t('form.applying') : t('filter.apply')}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          {t('form.cancel')}
        </button>
      </div>
    </form>
  );
};

export default FilterOperation;